// src/Redux/slices/userSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/* ──────────────────────────────────────────────────────────────────────────
 * Permission key types (keep in sync with backend model)
 * ────────────────────────────────────────────────────────────────────────── */
export type CompanyPermKey =
  | 'enrollSites'
  | 'deleteSites'
  | 'editSites'
  | 'viewSites'
  | 'enrollUsers'
  | 'deleteUsers'
  | 'editUsers'
  | 'viewUsers'
  | 'addFiles'
  | 'createLayouts'
  | 'editLayouts'
  | 'deleteLayouts'
  | 'viewLayouts';

export type SitePermKey =
  | 'editSites'
  | 'viewSites'
  | 'enrollUsers'
  | 'deleteUsers'
  | 'editUsers'
  | 'viewUsers'
  | 'addFiles'
  | 'createLayouts'
  | 'editLayouts'
  | 'deleteLayouts'
  | 'viewLayouts';

export type CompanyPermissions = Record<CompanyPermKey, boolean>;
export type SitePermissions = Record<SitePermKey, boolean>;

export const COMPANY_PERM_DEFAULTS: CompanyPermissions = {
  enrollSites: false,
  deleteSites: false,
  editSites: false,
  viewSites: false,
  enrollUsers: false,
  deleteUsers: false,
  editUsers: false,
  viewUsers: false,

  addFiles: false,

  createLayouts: false,
  editLayouts: false,
  deleteLayouts: false,
  viewLayouts: false,
};

export const SITE_PERM_DEFAULTS: SitePermissions = {
  editSites: false,
  viewSites: false,

  enrollUsers: false,
  deleteUsers: false,
  editUsers: false,
  viewUsers: false,

  addFiles: false,

  createLayouts: false,
  editLayouts: false,
  deleteLayouts: false,
  viewLayouts: false,
};

/* ──────────────────────────────────────────────────────────────────────────
 * State
 * ────────────────────────────────────────────────────────────────────────── */
interface GlobalState {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  assignedSites: string[];
  userType: string;
  companyPermissions: CompanyPermissions;
  sitePermissions: SitePermissions; // ← flat object, same as backend
}

const initialState: GlobalState = {
  _id: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  assignedSites: [],
  userType: '',
  companyPermissions: { ...COMPANY_PERM_DEFAULTS },
  sitePermissions: { ...SITE_PERM_DEFAULTS },
};

/* ──────────────────────────────────────────────────────────────────────────
 * Slice
 * ────────────────────────────────────────────────────────────────────────── */
export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // Populate the whole user object from API payload
    setUser: (state, action: PayloadAction<any>) => {
      const p = action.payload ?? {};
      state._id = p._id ?? '';
      state.firstName = p.firstName ?? '';
      state.lastName = p.lastName ?? '';
      state.email = p.email ?? '';
      state.phone = p.phone ?? '';

      // Accept either `assignedSites` (backend) or legacy `approvedSites` (frontend)
      state.assignedSites = p.assignedSites ?? p.approvedSites ?? [];

      state.userType = p.userType ?? '';

      // Permissions (fallback to defaults if missing)
      state.companyPermissions = {
        ...COMPANY_PERM_DEFAULTS,
        ...(p.companyPermissions ?? {}),
      } as CompanyPermissions;

      state.sitePermissions = {
        ...SITE_PERM_DEFAULTS,
        ...(p.sitePermissions ?? {}),
      } as SitePermissions;
    },

    setFirstName: (state, action: PayloadAction<string>) => {
      state.firstName = action.payload;
    },
    setLastName: (state, action: PayloadAction<string>) => {
      state.lastName = action.payload;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setPhone: (state, action: PayloadAction<string>) => {
      state.phone = action.payload;
    },
    setApprovedSites: (state, action: PayloadAction<string[]>) => {
      state.assignedSites = action.payload;
    },

    // Replace whole permission objects
    setCompanyPermissions: (
      state,
      action: PayloadAction<Partial<CompanyPermissions>>
    ) => {
      state.companyPermissions = {
        ...state.companyPermissions,
        ...action.payload,
      };
    },
    setSitePermissions: (
      state,
      action: PayloadAction<Partial<SitePermissions>>
    ) => {
      state.sitePermissions = {
        ...state.sitePermissions,
        ...action.payload,
      };
    },

    // Toggle single flags (convenience)
    setCompanyPermissionFlag: (
      state,
      action: PayloadAction<{ key: CompanyPermKey; value: boolean }>
    ) => {
      state.companyPermissions[action.payload.key] = action.payload.value;
    },
    setSitePermissionFlag: (
      state,
      action: PayloadAction<{ key: SitePermKey; value: boolean }>
    ) => {
      state.sitePermissions[action.payload.key] = action.payload.value;
    },

    resetUserForm: (state) => {
      state.firstName = '';
      state.lastName = '';
      state.email = '';
      state.phone = '';
      state.assignedSites = [];
      state.userType = '';
      state.companyPermissions = { ...COMPANY_PERM_DEFAULTS };
      state.sitePermissions = { ...SITE_PERM_DEFAULTS };
    },
  },
});

export const {
  setUser,
  setFirstName,
  setLastName,
  setEmail,
  setPhone,
  setApprovedSites,
  setCompanyPermissions,
  setSitePermissions,
  setCompanyPermissionFlag,
  setSitePermissionFlag,
  resetUserForm,
} = userSlice.actions;

export default userSlice.reducer;

/* ──────────────────────────────────────────────────────────────────────────
 * Optional selectors
 * ────────────────────────────────────────────────────────────────────────── */
// Example: OR-merge company + site permissions like backend getEffectivePermissions
export const selectEffectivePermissions = (state: {
  user: GlobalState;
}): SitePermissions => {
  const comp = state.user.companyPermissions;
  const site = state.user.sitePermissions;
  return {
    editSites: comp.editSites || site.editSites,
    viewSites: comp.viewSites || site.viewSites,
    enrollUsers: comp.enrollUsers || site.enrollUsers,
    deleteUsers: comp.deleteUsers || site.deleteUsers,
    editUsers: comp.editUsers || site.editUsers,
    viewUsers: comp.viewUsers || site.viewUsers,
    addFiles: comp.addFiles || site.addFiles,
    createLayouts: comp.createLayouts || site.createLayouts,
    editLayouts: comp.editLayouts || site.editLayouts,
    deleteLayouts: comp.deleteLayouts || site.deleteLayouts,
    viewLayouts: comp.viewLayouts || site.viewLayouts,
  };
};
